import { useEffect } from 'react';
import './PrivacyPolicy.css';
import { logEvent } from '../utils/logger';

function PrivacyPolicy() {
  useEffect(() => {


    logEvent({
      event: {
        label: 'PageView',
        data: { page: 'Privacy Policy' },
      },
    });
  }, []);

  return (
    <div className="page-content privacy-page">
      <article className="privacy-policy-container">
        <header className="privacy-policy-header">
          <h1>Privacy Policy</h1>
          <p>How Sayable.ai collects, uses, and protects information across our website and platform.</p>
          <div className="privacy-effective-date">
            <strong>Effective date</strong>
            <time dateTime="2026-09-13">September 13, 2026</time>
          </div>
        </header>

        <h2>Introduction</h2>
        <p>
          At Sayable.ai, we are committed to safeguarding the personal information of visitors to our website at <a href="https://www.sayable.ai">www.sayable.ai</a> (the “Site”) and users (“Users”) of the Sayable.ai platform (collectively referred to as “you” or “your”). This Privacy Policy outlines our practices regarding the collection, use, and disclosure of personal information through the Site and the Sayable.ai platform (collectively, the “Services”).
        </p>

        <h2>Scope</h2>
        <p>
          This Privacy Policy is applicable to the Services, including any mobile applications and other interactive features controlled by Sayable.ai that link to this Privacy Policy. When our assistant handles calls, messages, or appointments for a business customer, we process that information on the customer’s behalf. This can include contact details, conversation content, recordings where enabled, transcripts, summaries, and appointment information. The business customer’s privacy notices also apply to its use of that information. The Google data protections below apply whenever we process Google user data through our Services.
        </p>

        <h2>Our Commitment</h2>
        <p>
          We prioritize transparency in how we handle your personal information. Before engaging with our Services, please review this Privacy Policy carefully. By using the Services, you consent to the practices detailed here. If you disagree with any part of this policy, please refrain from using our Services.
        </p>

        <h2>Information Collection</h2>
        <p>
          We gather information that could potentially identify you, either alone or in conjunction with other data we hold. This includes personal details such as your name, email address, phone number, and account credentials; financial information processed by a third-party for transactions; communications shared with us; social media and internet activity data; and information you provide through connected services, as described below.
        </p>

        <h2>Cookies and Analytics</h2>
        <p>
          We use cookies and analytics tools like Google Analytics to enhance user experience and analyze website usage. You have control over cookie settings and can opt out of targeted advertising through various resources.
        </p>

        <h2>Usage of Information</h2>
        <p>
          Your personal data is used to provide and improve our Services, process transactions, communicate with you, comply with legal obligations, and for marketing purposes, subject to your opt-out rights.
        </p>

        <h2>Sharing of Information</h2>
        <p>
          We may share your information with our customers, service providers, for legal and administrative reasons, during business transitions, and with your consent for other purposes. We also share aggregated or anonymized information that does not personally identify you.
        </p>

        <h2>Children's Privacy</h2>
        <p>
          Our Services are not intended for people under 18, and we do not knowingly collect data from this age group.
        </p>

        <h2>Google Account and Calendar Data</h2>
        <p>
          If you choose Google sign-in, we receive your Google account identifier, name, email address, and basic profile information to verify your identity and create, sign in to, or link your Sayable account. Signing in with Google does not by itself grant access to your calendar.
        </p>
        <p>
          If you separately connect Google Calendar, we access your calendar list, calendar identifiers, availability, and event information, which may include titles, descriptions, dates, times, locations, and attendees. We use this information to identify your calendar, check availability, avoid scheduling conflicts, and manage appointments on your behalf. The permissions you grant may allow us to create, update, and delete events on calendars you own. We store connection credentials so scheduling can continue without asking you to sign in for each appointment. We do not receive your Google password.
        </p>

        <h2>How We Use and Share Google Data</h2>
        <p>
          We use Google user data only to provide or improve the connected, user-facing features you choose, such as account access and appointment scheduling. Relevant availability and appointment information may be processed by our AI service providers, including OpenAI, to understand scheduling requests and carry them out. Service providers supporting hosting and operation of these features may also process this data. Appointment information is shared with the connected calendar and the business or participants involved as needed to fulfill your request.
        </p>
        <p>
          We do not sell Google user data or use or share it for advertising, credit decisions, or unrelated purposes. We do not use Google Workspace API data to develop, improve, or train non-personalized AI or machine-learning models, and we do not permit its transfer for those purposes. These restrictions also apply to aggregated, anonymized, or derived Google data.
        </p>
        <p>
          Additional disclosures of Google data are limited to security purposes, compliance with applicable law, or a business transfer with your explicit prior consent. Human access is limited to your affirmative agreement, necessary security investigations, legal obligations, or aggregated data used for internal operations as permitted by Google’s policy. These Google-specific restrictions take precedence over the general information-use and sharing provisions above.
        </p>
        <p>
          Sayable.ai’s use and transfer to any other app of information received from Google APIs will adhere to the <a href="https://developers.google.com/terms/api-services-user-data-policy">Google API Services User Data Policy</a>, including the Limited Use requirements.
        </p>

        <h2>Data Retention and Deletion</h2>
        <p>
          We retain personal information, including Google account and calendar information, for as long as needed to provide the Services you use and fulfill the purposes described in this policy. Connection credentials are retained to maintain your authorized connection. Some records may need to be retained longer to meet legal, accounting, dispute-resolution, or security obligations. Retention depends on the type of record and its purpose.
        </p>
        <p>
          You can revoke Sayable’s Google access at any time through your <a href="https://myaccount.google.com/connections">Google Account connections</a>. Revoking access stops future access through that authorization; it does not automatically delete information already stored by Sayable or events already created in your Google Calendar. Google sign-in and Calendar are separate features; disconnecting sign-in within Sayable does not disconnect Calendar.
        </p>
        <p>
          You can delete your account from Account settings. Organization administrators delete the organization and its member accounts; other members delete only their personal account. For assistance or to request deletion of specific stored Google data or connection credentials, email <a href="mailto:support@sayable.ai">support@sayable.ai</a>. We verify your authority before acting on a request and delete the requested information unless retention is required for the purposes above. Retained records remain subject to this policy. For information held on behalf of a business customer, contact that business or contact us for assistance directing your request. Events remaining in Google Calendar can be managed or deleted in Google Calendar.
        </p>

        <h2>Security Measures</h2>
        <p>
          We use encrypted connections for data transmitted between you, Sayable, and Google, together with authentication and access controls to help protect personal information, including Google user data, from unauthorized access. No method of transmission or storage is completely secure.
        </p>

        <h2>International Users</h2>
        <p>
          Our Services are based in the United States. International users should be aware that their data will be transferred to and processed in the U.S.
        </p>

        <h2>Changes to Privacy Policy</h2>
        <p>
          We may update this policy from time to time. The effective date will be adjusted accordingly. We will notify you of material changes to how we handle your information. If we change how we access or use Google user data, we will provide notice and obtain any required consent before the new use begins.
        </p>

        <div className="privacy-footer">
          <p>
            If you have any questions or concerns about this Privacy Policy, please contact us at <a href="mailto:support@sayable.ai">support@sayable.ai</a>.
          </p>
        </div>
      </article>
    </div>
  );
}

export default PrivacyPolicy;
